import {
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { Button } from "@/src/components/ui/button";
import { Delete, Get, Post } from "@/src/api";
import React, { useState } from "react";
import AccountManagerDetails from "./AccountManagerDetails";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useConfirmDialog } from "@/src/components/confirmDialog";
import { Pencil, Trash2 } from "lucide-react";
import CreateAccountManagerDialog from "./CreateAccountManagerDialog";

type AccountManager = {
  firstName: string;
  lastName: string;
  mail: string;
  id: number;
};

const AccountManagers = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [isCreating, setCreating] = useState(false);

  const {
    isOpen: isDetailsOpen,
    onOpen: onDetailsOpen,
    onClose: onDetailsClose,
  } = useDisclosure();
  const [selectedAccountManagerId, setSelectedAccountManagerId] = useState<
    number | null
  >(null);

  const { data: accountManagers = [] } = useQuery<AccountManager[]>({
    queryKey: ["accountManagers"],
    queryFn: async () => {
      const result = await Get(
        "/support/admin/account-managers?page=1&limit=999",
      );
      return result as AccountManager[];
    },
  });

  const createAccountManagerMutation = useMutation({
    mutationFn: async (data: {
      firstName: string;
      lastName: string;
      mail: string;
      password: string;
    }) => {
      return Post("/support/admin/account-managers", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accountManagers"] });
      toast({
        title: "Account Manager Created",
        description: "Account Manager has been created successfully.",
        status: "success",
        duration: 9000,
        isClosable: true,
      });
      setCreating(false);
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      toast({
        title: "Error",
        description: `Account manager couldn't be created. ${errorMessage}`,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    },
  });

  const { ConfirmDialog: DeleteConfirmDialog, openDialog: openDeleteDialog } =
    useConfirmDialog({
      title: "Delete Account Manager",
      description:
        "Are you sure you want to delete this account manager? This action cannot be undone.",
      destructive: true,
    });

  const deleteAccountManagerMutation = useMutation({
    mutationFn: async (id: number) => {
      return Delete(`/support/admin/account-managers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accountManagers"] });
      toast({
        title: "Account Manager Deleted",
        description: "Account Manager has been deleted successfully.",
        status: "success",
        duration: 9000,
        isClosable: true,
      });
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      toast({
        title: "Error",
        description: `Account manager couldn't be deleted. ${errorMessage}`,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    },
  });

  const textColor = useColorModeValue("secondaryGray.900", "white");

  const handleDelete = async (managerId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const confirmed = await openDeleteDialog();
    if (confirmed) {
      deleteAccountManagerMutation.mutate(managerId);
    }
  };

  const handleEdit = (managerId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedAccountManagerId(managerId);
    onDetailsOpen();
  };

  return (
    <>
      <Flex
        flex={1}
        flexDir="column"
        minH={"90vh"}
        data-testid="account-managers-container"
      >
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <div />
          <Button onClick={() => setCreating(true)}>
            Create New Account Manager
          </Button>
        </Flex>

        <Table
          variant="striped"
          color="gray.500"
          mb="24px"
          backgroundColor="white"
        >
          <Thead>
            <Tr>
              <Th>First Name</Th>
              <Th>Last Name</Th>
              <Th>Email</Th>
              <Th width="100px">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {accountManagers?.map((manager: AccountManager) => (
              <Tr key={manager.id}>
                <Td>
                  <div>
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {manager.firstName}
                    </Text>
                  </div>
                </Td>
                <Td>
                  <div>
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {manager.lastName}
                    </Text>
                  </div>
                </Td>
                <Td>
                  <div>
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {manager.mail}
                    </Text>
                  </div>
                </Td>
                <Td>
                  <Flex gap={2} justifyContent="flex-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => handleEdit(manager.id, e)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => handleDelete(manager.id, e)}
                      disabled={deleteAccountManagerMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        <CreateAccountManagerDialog
          open={isCreating}
          onOpenChange={setCreating}
          onSubmit={createAccountManagerMutation.mutate}
          isLoading={createAccountManagerMutation.isPending}
        />

        <AccountManagerDetails
          isOpen={isDetailsOpen}
          onClose={onDetailsClose}
          accountManagerId={selectedAccountManagerId}
        />
        {DeleteConfirmDialog}
      </Flex>
    </>
  );
};

export default AccountManagers;
